<script lang="ts">
	//Formating
	import { format } from 'svelte-i18n';
	//Design system components
	import { Input, Button, Toggle, Checkbox, IconButton } from '@Shelly/shelly-design-system';
	//Helpers
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import {
		getErrorOrInfoMessageForMinAndMaxUnit,
		valueOutOfDecimalRange,
	} from 'src/helpers/utilities';
	import { addNumberStep } from 'src/helpers/number_input_helpers';
	import { getTemplateService } from 'src/helpers/xt1_helpers/template_helpers';
	import IrrigationConfirmModal from '../Irrigation/IrrigationConfirmModal.svelte';
	//Types
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';

	export let device: Device;
	export let setting: string;
	export let min: number;
	export let max: number;
	export let step: number = 1;
	export let value: number = 0;
	export let unit: string = '';
	export let icon: string = 'fas fa-calculator';
	export let confirmModal: IrrigationConfirmModal = null;
	export let minDecimalDigits: number = null;
	export let disabled: boolean = false;
	export let withToggle: boolean = false;
	export let withCheckbox: boolean = false;

	type MinMaxInfo = { error: string; info: string };

	let minMaxInfo: MinMaxInfo = { error: '', info: '' };
	let targetLock = false;
	let waitForResponse = false;

	const onSettingChange = (device: Device) => {
		const _value: number = getTemplateService(device)?.settings?.[setting];
		if (!waitForResponse && !targetLock && value !== _value) {
			value = _value;
			disabled = (withToggle || withCheckbox) && _value === null;
		}
		if (targetLock && value == _value) {
			targetLock = false;
		}
	};

	const saveValue = async () => {
		const _value = value;
		if (confirmModal && !(await confirmModal.confirm())) {
			value = _value;
			targetLock = false;
			return;
		}
		waitForResponse = true;
		const res: SettingRequest = await gen2Request(device, 'Service.SetConfig', {
			id: 0,
			config: {
				[`${setting}`]: (withToggle || withCheckbox) && disabled ? null : Number(value),
			},
		});
		if (res?.isok) {
			targetLock = true;
			value = _value;
		}
		onSettingsResponse(res, device);
		waitForResponse = false;
	};

	const valueOutOfRange = (value: number | string): MinMaxInfo => {
		const minMaxInfo = getErrorOrInfoMessageForMinAndMaxUnit(
			value === '' ? NaN : Number(value),
			min,
			max,
			unit,
		);
		if (!minMaxInfo.error && minDecimalDigits !== null) {
			const decimalError = valueOutOfDecimalRange(String(value), minDecimalDigits);
			if (decimalError) minMaxInfo.error = decimalError;
		}
		return minMaxInfo;
	};

	/**
	 * Handle user input change
	 * @param {string} val
	 */
	const handleChange = (val: number) => {
		targetLock = true;
		value = val;
	};

	$: minMaxInfo = valueOutOfRange(value);
	$: onSettingChange(device);
	onSettingChange(device);
</script>

{#if withToggle}
	<Toggle
		class="mb-s"
		size="m"
		checked={!disabled}
		on:click={() => {
			disabled = !disabled;
			targetLock = true;
			if (!disabled && value === null) value = 0;
		}}
		id="toggle-6u2twma"
	>
		{$format('enable', { default: 'Enable' })}
	</Toggle>
{:else if withCheckbox}
	<Checkbox
		class="mb-s"
		size="l"
		checked={!disabled}
		on:click={() => {
			disabled = !disabled;
			targetLock = true;
			if (!disabled && value === null) value = 0;
		}}
		id="checkbox-vqbgxns"
	>
		{$format('enable', { default: 'Enable' })}
	</Checkbox>
{/if}
<slot />
<div class="d-flex mb-s">
	<IconButton
		aria-label={$format('button_decrease', { default: 'Decrease' })}
		icon="fas fa-minus"
		on:click={() => {
			handleChange(addNumberStep(+value, -step));
		}}
		disabled={+value <= min}
	/>
	<Input
		aria-label={$format('input_set_value', { default: 'Set value' })}
		class="width-md-l width-sm-full ml-s mr-s"
		type="number"
		size="m"
		{icon}
		value={disabled ? '' : (value?.toString() ?? '')}
		{disabled}
		info={minMaxInfo.info}
		error={minMaxInfo.error}
		on:input={(e) => {
			handleChange(e.target['value']);
		}}
		id="input-l3o8owi"
	/>
	<IconButton
		aria-label={$format('button_increase', { default: 'Increase' })}
		icon="fas fa-plus"
		on:click={() => {
			handleChange(addNumberStep(+value, step));
		}}
		disabled={+value >= max}
	/>
</div>
<Button loading={waitForResponse} size="m" width="full" on:click={saveValue} disabled={minMaxInfo.error}
	>{$format('button_save')}</Button
>
